<template>
  <mp-box class="input-scope">
    <mp-accordion allow-toggle :index="activeIndex">
      <mp-accordion-item>
        <mp-accordion-header text-align="left" py="2" :data-qa="`accordion-scope-${label.toLowerCase().replace(/ /g, '-')}`" @click="toggleAccordion">
          <mp-flex justify-content="space-between" align-items="center" w="full" gap="1">
            <mp-box>
              <mp-text>{{ label }}</mp-text>
              <mp-text font-size="sm" color="gray.600">{{ selectedPreview }}</mp-text>
            </mp-box>
            <mp-flex w="64px" gap="1" align-items="center" justify-content="flex-end">
              <mp-button-icon
                v-mp-tooltip="'Remove'"
                name="minus-circular"
                :data-qa="`btn-remove-scope-${label.toLowerCase().replace(/ /g, '-')}`"
                @click.stop="handleRemove"
              />
              <mp-accordion-icon name="chevrons-down" @click.native="toggleAccordion" />
            </mp-flex>
          </mp-flex>
        </mp-accordion-header>

        <mp-accordion-panel py="0" text-align="left">
          <mp-flex gap="2" direction="column">
            <!-- Search -->
            <input-search v-model="search" placeholder="Search" is-full-width />

            <!-- Selected info -->
            <mp-text color="gray.600">{{ value.length | pluralize(`selected ${label.toLowerCase()}`, `selected ${pluralizedLabel}`, 'No') }}</mp-text>

            <!-- Error message -->
            <mp-text v-if="isError" color="red.400" font-size="sm" data-element="error-message">{{ label }} must be selected at least one.</mp-text>

            <!-- Checkbox area -->
            <mp-box>
              <mp-checkbox v-model="isSelectAll" :is-indeterminate="isIndeterminate" :is-invalid="isError" @change="handleSelectAll">
                <mp-text>Select all {{ pluralizedLabel }}</mp-text>
              </mp-checkbox>

              <mp-box max-h="200px" overflow-y="auto" @scroll.native="onScroll">
                <mp-checkbox-group :value="value" name="checkbox-input-scope" @change="handleSelect">
                  <!-- Pixel always convert value to string, so toString just to made it consistent -->
                  <mp-checkbox v-for="item in filteredItems" :key="item.id" :value="item.id.toString()" m="0" :is-invalid="isError">
                    {{ item.name }}
                  </mp-checkbox>
                </mp-checkbox-group>

                <mp-box v-if="filteredItems.length === 0" py="4" text-align="center" color="gray.500">
                  No items found
                </mp-box>
              </mp-box>
            </mp-box>
          </mp-flex>
        </mp-accordion-panel>
      </mp-accordion-item>
    </mp-accordion>
  </mp-box>
</template>

<script>
import { debounce } from 'lodash-es';
import { pluralize } from '@/helpers/string';
import { formatCurrency } from '@/helpers/number';
import InputSearch from '@/components/InputSearch';

export default {
  name: 'InputScope',
  components: {
    InputSearch,
  },
  filters: { pluralize },
  props: {
    label: {
      type: String,
      required: true,
    },
    isValidating: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      maxDisplayItems: 100, // Limit initial display
      itemIncrement: 100, // Load 100 more items at a time
      activeIndex: [0],
    };
  },
  watch: {
    isValidating(val) {
      if (val && this.value.length === 0) {
        // Force accordion open when validating and has error
        this.activeIndex = [0];
      }
    },
  },
  computed: {
    isError() {
      return this.isValidating && this.value.length === 0;
    },
    pluralizedLabel() {
      const label = this.label.toLowerCase();
      return label === 'branch' ? 'branches' : `${label}s`;
    },
    filteredItems() {
      if (!this.search) {
        // Show only first N items when not searching to improve initial render
        return this.items.slice(0, this.maxDisplayItems);
      }
      const searchLower = this.search.toLowerCase();
      // When searching, show all matching results (they'll be filtered down anyway)
      return this.items.filter(item => item.name.toLowerCase().includes(searchLower));
    },
    filteredIds() {
      // Cache filtered IDs to avoid re-mapping in multiple places
      return this.filteredItems.map(i => i.id.toString());
    },
    hasMoreItems() {
      return !this.search && this.items.length > this.maxDisplayItems;
    },
    isSelectAll: {
      get() {
        // When not searching, check if all items are selected
        if (!this.search) return this.items.length > 0 && this.value.length === this.items.length;

        // When searching, check if all filtered items are selected
        return this.filteredIds.length > 0 && this.filteredIds.every(id => this.value.includes(id));
      },
      set(val) {
        if (!this.search) {
          // When not searching, select/deselect all items
          if (val) {
            this.$emit(
              'input',
              this.items.map(i => i.id.toString()),
            );
          } else {
            this.$emit('input', []);
          }
        } else if (val) {
          // When searching, add filtered items to existing selection (merge and remove duplicates)
          const merged = [...new Set([...this.value, ...this.filteredIds])];
          this.$emit('input', merged);
        } else {
          // When searching, remove filtered items from existing selection
          const filtered = this.value.filter(id => !this.filteredIds.includes(id));
          this.$emit('input', filtered);
        }
      },
    },
    isIndeterminate() {
      // Indeterminate when some but not all items are selected
      if (!this.search) return this.value.length > 0 && this.value.length < this.items.length;
      // Handle indeterminate state when searching
      const selectedCount = this.filteredIds.reduce((count, id) => (this.value.includes(id) ? count + 1 : count), 0);
      return selectedCount > 0 && selectedCount < this.filteredIds.length;
    },
    selectedPreview() {
      if (this.value.length === 0 || this.activeIndex.length === 1) return 'is any of';

      // Get the first 3 selected items
      const selectedItems = this.items.filter(item => this.value.includes(item.id.toString())).slice(0, 3);
      const names = selectedItems.map(item => item.name);

      if (this.value.length === 1) {
        return `is any of ${names[0]}`;
      }

      if (this.value.length === 2) {
        return `is any of ${names[0]} or ${names[1]}`;
      }

      if (this.value.length === 3) {
        return `is any of ${names[0]}, ${names[1]} or ${names[2]}`;
      }

      // More than 3 items
      return `is any of ${names[0]}, ${names[1]}, ${names[2]} and ${formatCurrency(this.value.length - 3)} others`;
    },
  },
  methods: {
    toggleAccordion() {
      this.activeIndex = this.activeIndex[0] === 0 ? [] : [0];
    },
    handleSelect(val) {
      this.$emit('input', val);
    },
    handleSelectAll() {
      this.isSelectAll = !this.isSelectAll;
    },
    handleRemove() {
      this.$emit('remove');
    },
    loadMore() {
      this.maxDisplayItems = Math.min(this.maxDisplayItems + this.itemIncrement, this.items.length);
    },
    onScroll: debounce(function(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target;
      const pixelTolerance = 30;
      // Auto-load more when scrolling near bottom
      if (this.hasMoreItems && scrollTop + clientHeight + pixelTolerance >= scrollHeight) {
        this.loadMore();
      }
    }, 400),
  },
};
</script>
